<template>
    <b-card-code
        title="Update Router Info"
        class="router-edit-card"
    >
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-2 router-edit-header">
            <div>
                <h4 class="mb-0">{{ routerInfo && routerInfo.router_name ? routerInfo.router_name : 'Router Details' }}</h4>
                <small class="text-muted">Update connection settings and credentials</small>
            </div>
            <b-badge
                v-if="statusLabel"
                :variant="statusBadgeVariant(routerInfo.status)"
                class="text-uppercase"
            >
                {{ statusLabel }}
            </b-badge>
        </div>
        <validation-observer ref="simpleRules">
            <b-form>
                <div v-if="isLoading" class="router-edit-loading">
                    <div class="router-edit-loading__bar"></div>
                    <div class="router-edit-loading__bar"></div>
                    <div class="router-edit-loading__bar"></div>
                </div>
                <div v-else>
                <div class="router-edit-section">
                    <div class="router-edit-section__title">Connection</div>
                    <div class="router-edit-section__desc text-muted">Router identity and network endpoint</div>
                    <b-row>
                        <b-col md="6">
                            <b-form-group
                                label="Route Name"
                                label-for="route-name">
                                <validation-provider
                                    #default="{ errors }"
                                    name="Route Name"
                                    rules="required"
                                >
                                    <b-form-input
                                        v-model="routerInfo.router_name"
                                        :state="errors.length > 0 ? false:null"
                                        placeholder="Enter Route Name"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group
                                label="IP Address"
                                label-for="id-address">
                                <validation-provider
                                    #default="{ errors }"
                                    name="IP Address"
                                    rules="required"
                                >
                                    <b-form-input
                                        v-model="routerInfo.ip_address"
                                        :state="errors.length > 0 ? false:null"
                                        placeholder="Enter IP Address"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group
                                label="IP Port"
                                label-for="id-port">
                                <validation-provider
                                    #default="{ errors }"
                                    name="Port"
                                    rules="required"
                                >
                                    <b-form-input
                                        type="number"
                                        v-model="routerInfo.api_port"
                                        :state="errors.length > 0 ? false:null"
                                        placeholder="Enter IP Port"
                                    />
                                    <small class="text-muted">Default MikroTik API port is 8728.</small>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>
                <div class="router-edit-section">
                    <div class="router-edit-section__title">Credentials</div>
                    <div class="router-edit-section__desc text-muted">Authentication for MikroTik API access</div>
                    <b-row>
                        <b-col md="6">
                            <b-form-group
                                label="UserName"
                                label-for="username">
                                <validation-provider
                                    #default="{ errors }"
                                    name="UserName"
                                    rules="required"
                                >
                                    <b-form-input
                                        v-model="routerInfo.username"
                                        :state="errors.length > 0 ? false:null"
                                        placeholder="Enter Router User Name"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group
                                label="Password"
                                label-for="password">
                                <validation-provider
                                    #default="{ errors }"
                                    name="Password"
                                    rules="required"
                                >
                                    <div class="d-flex align-items-center">
                                        <b-form-input
                                            :type="showPassword ? 'text' : 'password'"
                                            v-model="routerInfo.password"
                                            :state="errors.length > 0 ? false:null"
                                            placeholder="Enter Router Password"
                                        />
                                        <b-button
                                            variant="outline-secondary"
                                            size="sm"
                                            class="ml-50"
                                            @click="showPassword = !showPassword"
                                        >
                                            <feather-icon :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'" size="14" />
                                        </b-button>
                                    </div>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>
                <div class="router-edit-section">
                    <div class="router-edit-section__title">Status</div>
                    <div class="router-edit-section__desc text-muted">Enable or disable this router</div>
                    <b-row>
                        <b-col md="6">
                            <b-form-group
                                label="Status"
                                label-for="status">
                                <validation-provider
                                    #default="{ errors }"
                                    name="Status"
                                    rules="required"
                                >
                                    <b-form-checkbox
                                        v-model="routerInfo.status"
                                        switch
                                        :value="'1'"
                                        :unchecked-value="'0'"
                                    >
                                        {{ routerInfo.status === '1' ? 'Active' : 'Inactive' }}
                                    </b-form-checkbox>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>
                <div class="d-flex justify-content-end">
                    <b-button
                        variant="outline-secondary"
                        class="mr-1"
                        @click="goBack"
                    >
                        Cancel
                    </b-button>
                    <b-button
                        type="submit"
                        variant="primary"
                        :disabled="isSubmitting"
                        @click.prevent="validationForm"
                    >
                        {{ isSubmitting ? 'Saving...' : 'Submit' }}
                    </b-button>
                </div>
                </div>
            </b-form>
        </validation-observer>

    </b-card-code>
</template>

<script>
import BCardCode from '@core/components/b-card-code'
import {ValidationProvider, ValidationObserver} from 'vee-validate'
import {
    BFormInput, BFormGroup, BForm, BRow, BCol, BButton, BCardText, BFormCheckbox, BBadge,
} from 'bootstrap-vue'
import {required, email} from '@validations'
import useJwt from "@/auth/jwt/useJwt";
import router from "@/router";
import { ROUTER_VIEW} from "@core/services/api";
import {checkRes} from "@core/services/helper";
import Mixin from "@core/services/mixin";

export default {
    mixins: [
        Mixin
    ],
    components: {
        BFormCheckbox,
        BBadge,
        BCardCode,
        ValidationProvider,
        ValidationObserver,
        BCardText,
        BFormInput,
        BFormGroup,
        BForm,
        BRow,
        BCol,
        BButton,
    },
    data() {
        return {
            routerId: router.currentRoute.params.routerId,
            routerInfo: 'null',
            showPassword: false,
            isLoading: false,
            isSubmitting: false,
            emailValue: '',
            name: '',
            required,
            email,
        }
    },
    computed: {
        statusLabel() {
            return this.resolveStatusLabel(this.routerInfo && this.routerInfo.status)
        },
        statusBadgeVariant() {
            return status => (this.resolveStatusLabel(status) === 'Active' ? 'light-success' : 'light-secondary')
        },
    },
    methods: {
        resolveStatusLabel(value) {
            if (value === undefined || value === null) return ''
            if (value === '1' || value === 1) return 'Active'
            if (value === '0' || value === 0) return 'Inactive'
            if (value === 'Active' || value === 'Inactive') return value
            return ''
        },
        goBack() {
            this.$router.push({ name: 'isp-router-list' }).catch(() => {})
        },
        getRouterData() {
            this.isLoading = true
            this.$http.get(process.env.VUE_APP_BASEURL + ROUTER_VIEW + this.routerId)
                .then(res => {
                    this.routerInfo = res.data.data
                    const label = this.resolveStatusLabel(this.routerInfo && this.routerInfo.status)
                    if (label === 'Active') {
                        this.routerInfo.status = '1'
                    } else if (label === 'Inactive') {
                        this.routerInfo.status = '0'
                    }
                }).catch(error => {
                if (error.response.status === 404) {
                    userData.value = undefined
                }
            }).finally(() => {
                this.isLoading = false
            })
        },
        validationForm() {
            this.$refs.simpleRules.validate().then(success => {
                if (success) {
                    this.isSubmitting = true
                    useJwt.updateRouter(this.routerId, {
                        router_name: this.routerInfo.router_name,
                        ip_address: this.routerInfo.ip_address,
                        api_port: this.routerInfo.api_port,
                        username: this.routerInfo.username,
                        password: this.routerInfo.password,
                        status : this.routerInfo.status
                    }).then(response => {
                        if (checkRes(response.data.code)) {
                            this.$router.push({name: 'isp-router-edit'})
                                .catch(error => {
                                    this.toastMessage('success','Router Setting', response)
                                })
                        } else {
                            this.$router.push({name: 'isp-router-edit'})
                                .catch(error => {
                                    this.toastMessage('warning','Router Setting', response)
                                })
                        }
                    }).catch(error => {
                        console.log(error)
                    }).finally(() => {
                        this.isSubmitting = false
                    })
                }
            })
        },
    },
    mounted() {
        this.getRouterData()
    }
}
</script>

<style lang="scss">
.router-edit-card {
  .router-edit-header {
    padding-bottom: 0.75rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .router-edit-section {
    padding: 1rem;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 0.6rem;
    background: #fbfbfc;
    margin-bottom: 1.25rem;
  }

  .router-edit-section__title {
    font-weight: 600;
    font-size: 0.95rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 0.25rem;
  }

  .router-edit-section__desc {
    margin-bottom: 0.75rem;
  }

  .router-edit-loading {
    display: grid;
    gap: 0.75rem;
    padding: 1rem;
    border: 1px dashed rgba(0, 0, 0, 0.08);
    border-radius: 0.6rem;
    background: #f9fafb;
  }

  .router-edit-loading__bar {
    height: 14px;
    border-radius: 999px;
    background: linear-gradient(90deg, #eceff3 0%, #f7f8fa 50%, #eceff3 100%);
    background-size: 200% 100%;
    animation: router-edit-skeleton 1.2s ease-in-out infinite;
  }
}

@keyframes router-edit-skeleton {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
